import { useCallback, useEffect, useRef, useState } from 'react'
import Api from 'Vendor/Api'
import Import from 'Entity/Import'

export type ImportState = 'pending' | 'uploading' | 'uploaded' | 'processing' | 'success' | 'error'

const IMPORT_STORAGE_KEY_PREFIX = 'bimbo_active_import'
const IMPORT_LOCK_TTL_MS = 2 * 60 * 60 * 1000 // 2 hours

interface ActiveImportStorage {
    uuid: string
    timestamp: number
}

const getImportStorageKey = (userId?: number): string => {
    return userId ? `${IMPORT_STORAGE_KEY_PREFIX}_${userId}` : IMPORT_STORAGE_KEY_PREFIX
}

const getStoredActiveImport = (userId?: number): string | null => {
    try {
        const key = getImportStorageKey(userId)
        let raw = localStorage.getItem(key)
        if (!raw && userId) {
            raw = localStorage.getItem(IMPORT_STORAGE_KEY_PREFIX)
            if (raw) {
                localStorage.removeItem(IMPORT_STORAGE_KEY_PREFIX)
                localStorage.setItem(key, raw)
            }
        }
        if (!raw) {
            const legacy = localStorage.getItem('bimbo_active_import_uuid')
            if (legacy) {
                localStorage.removeItem('bimbo_active_import_uuid')
                return legacy
            }
            return null
        }
        try {
            const data: ActiveImportStorage = JSON.parse(raw)
            if (data?.uuid && data?.timestamp) {
                if (Date.now() - data.timestamp > IMPORT_LOCK_TTL_MS) {
                    localStorage.removeItem(key)
                    return null
                }
                return data.uuid
            }
        } catch {
            return raw
        }
    } catch {
        return null
    }
    return null
}

const saveStoredActiveImport = (uuid: string, userId?: number): void => {
    try {
        const key = getImportStorageKey(userId)
        const data: ActiveImportStorage = {
            uuid,
            timestamp: Date.now(),
        }
        localStorage.setItem(key, JSON.stringify(data))
    } catch {
        // ignore local storage errors
    }
}

const removeStoredActiveImport = (userId?: number): void => {
    try {
        localStorage.removeItem(getImportStorageKey(userId))
        localStorage.removeItem(IMPORT_STORAGE_KEY_PREFIX)
        localStorage.removeItem('bimbo_active_import_uuid')
    } catch {
        // ignore local storage errors
    }
}

export interface UseActiveImportOptions {
    userId?: number
    onFinalized?: (uuid: string) => void
    onFailed?: () => void
    onError?: () => void
}

export interface UseActiveImportResult {
    activeImportUuid: string | null
    isProcessing: boolean
    startActiveImport: (uuid: string) => void
    clearActiveImport: () => void
}

/**
 * Custom hook to manage active import state, storage TTL, and background status polling.
 */
export const useActiveImport = ({
    userId,
    onFinalized,
    onFailed,
    onError,
}: UseActiveImportOptions): UseActiveImportResult => {
    const [activeImportUuid, setActiveImportUuid] = useState<string | null>(() => {
        return getStoredActiveImport(userId)
    })

    const onFinalizedRef = useRef(onFinalized)
    const onFailedRef = useRef(onFailed)
    const onErrorRef = useRef(onError)

    useEffect(() => {
        onFinalizedRef.current = onFinalized
        onFailedRef.current = onFailed
        onErrorRef.current = onError
    })

    const clearActiveImport = useCallback(() => {
        removeStoredActiveImport(userId)
        setActiveImportUuid(null)
    }, [userId])

    const startActiveImport = useCallback((uuid: string) => {
        saveStoredActiveImport(uuid, userId)
        setActiveImportUuid(uuid)
    }, [userId])

    // Synchronize when userId changes (login / logout / switch)
    useEffect(() => {
        if (!userId) {
            setActiveImportUuid(null)
            return
        }

        const storedUuid = getStoredActiveImport(userId)
        if (storedUuid) {
            setActiveImportUuid(storedUuid)
        }
    }, [userId])

    // Background status polling with TTL, circuit-breaker, and unmount cancellation
    useEffect(() => {
        if (!activeImportUuid) {
            return
        }

        let consecutiveErrors = 0
        let isCancelled = false

        const checkStatus = () => {
            const storedUuid = getStoredActiveImport(userId)
            if (!storedUuid) {
                if (!isCancelled) {
                    clearActiveImport()
                }
                return
            }

            Api.get(`/import/${activeImportUuid}`)
                .then((response) => {
                    if (isCancelled) {
                        return
                    }
                    consecutiveErrors = 0
                    const data: Import = response.data
                    if (data?.status === 'finalized') {
                        clearActiveImport()
                        onFinalizedRef.current?.(activeImportUuid)
                        window.dispatchEvent(
                            new CustomEvent('bimbo:import-finalized', {
                                detail: { uuid: activeImportUuid },
                            })
                        )
                    } else if (data?.status === 'failed') {
                        clearActiveImport()
                        onFailedRef.current?.()
                    }
                })
                .catch((err) => {
                    if (isCancelled) {
                        return
                    }
                    if (err?.response?.status === 404) {
                        clearActiveImport()
                    } else {
                        consecutiveErrors += 1
                        if (consecutiveErrors >= 10) {
                            clearActiveImport()
                            onErrorRef.current?.()
                        }
                    }
                })
        }

        checkStatus()
        const timer = setInterval(checkStatus, 3000)

        return () => {
            isCancelled = true
            clearInterval(timer)
        }
    }, [activeImportUuid, clearActiveImport, userId])

    return {
        activeImportUuid,
        isProcessing: Boolean(activeImportUuid),
        startActiveImport,
        clearActiveImport,
    }
}

export default useActiveImport
